import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { brandStats, brandValues, testimonials } from '@/mocks/brand';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': 'https://rejiujiu.com/about/#webpage',
      url: 'https://rejiujiu.com/about',
      name: '关于热久久 - 品牌介绍 · 口碑评价 · 联系方式',
      isPartOf: { '@id': 'https://rejiujiu.com/#website' },
      about: { '@id': 'https://rejiujiu.com/#organization' },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://rejiujiu.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://rejiujiu.com/about' },
        ],
      },
    },
  ],
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9sr8ph4ne17ei0sdmjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
          setFormStatus('error');
        } else {
          setFormError(serverMsg);
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Hero */}
          <section className="py-16 md:py-24 bg-background-100 relative overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20abstract%20artistic%20background%20with%20soft%20golden%20amber%20gradients%20and%20gentle%20curves%2C%20minimalist%20elegant%20design%2C%20warm%20cream%20to%20deep%20amber%20transition%2C%20peaceful%20comforting%20atmosphere%2C%20subtle%20texture%20like%20soft%20fabric&width=1920&height=500&seq=about-hero-rejiujiu&orientation=landscape"
                alt="热久久品牌"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-secondary-900/80 to-secondary-900/40"></div>
            </div>
            <div className="relative z-10 w-full px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <nav className="flex items-center gap-2 text-xs text-background-50/60 mb-6">
                  <Link to="/" className="hover:text-background-50 transition-colors whitespace-nowrap">首页</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-background-50/80 font-medium whitespace-nowrap">关于我们</span>
                </nav>
                <h1 className="text-2xl md:text-3xl font-bold text-background-50 mb-3 font-heading">
                  关于热久久
                </h1>
                <p className="text-sm md:text-base text-background-50/70 max-w-[600px]">
                  专为寒冷环境打造的持久保暖品牌，12年深耕发热科技领域
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 bg-background-50 border-b border-background-200/70">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {brandStats.map((stat) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-2xl md:text-3xl font-bold text-primary-600 font-heading">{stat.value}</div>
                      <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6 font-heading">
                      <a href="#brand-story">品牌故事</a>
                    </h2>
                    <div className="space-y-4">
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        热久久创立于2014年，品牌名称寓意"热爱久久，温暖久久"。创立之初，我们便立志解决寒冷环境下的保暖难题，让每一位在寒冬中工作、生活、出行的人都能感受到持久的温暖。
                      </p>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        经过12年的技术积累与产品迭代，热久久已从最初的单一保暖贴产品，发展到如今涵盖保暖贴、户外取暖、日常暖身、冬季装备四大系列的全品类保暖品牌。我们始终坚持以用户需求为导向，以科技创新为驱动。
                      </p>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        热久久拥有50余项专利技术，产品远销海内外多个国家和地区，累计服务超过500万用户。未来，热久久将继续深耕发热科技领域，让温暖触手可及，让每一次寒冷都变成温暖记忆。
                      </p>
                    </div>
                  </div>
                  <div className="rounded-xl overflow-hidden aspect-[4/3] bg-background-100">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20laboratory%20with%20warm%20amber%20lighting%2C%20product%20research%20and%20development%20setting%2C%20clean%20professional%20environment%2C%20warm%20wood%20tones%20and%20soft%20natural%20light%2C%20innovative%20technology%20company%20aesthetic%2C%20editorial%20style%20photography&width=800&height=600&seq=about-story-rejiujiu&orientation=landscape"
                      alt="热久久品牌故事 - 温暖科技研发"
                      title="热久久品牌故事"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Brand Values */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading">
                    <a href="#brand-values">品牌理念</a>
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 max-w-[500px] mx-auto">
                    安全、持久、环保、贴心——四大核心价值观，驱动热久久不断前行
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {brandValues.map((value, index) => (
                    <div key={index} className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-100 flex items-center justify-center">
                        <i className={`${value.icon} text-2xl text-primary-600`}></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{value.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading">
                    <a href="#about-testimonials">用户口碑</a>
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 max-w-[500px] mx-auto">
                    数百万用户的选择与信赖，真实评价见证品质
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {testimonials.map((t) => (
                    <div key={t.id} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-sm text-primary-500"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4 line-clamp-5">"{t.content}"</p>
                      <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                        <div className="w-9 h-9 rounded-full bg-primary-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                          <span className="text-xs font-bold text-primary-700">{t.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">{t.name}</p>
                          <p className="text-xs text-foreground-500 whitespace-nowrap">{t.role} · {t.location}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[600px] mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading">
                    <a href="#contact">联系我们</a>
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600">
                    如果您有任何问题或合作意向，欢迎随时与我们联系
                  </p>
                </div>

                <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
                  <div className="flex flex-wrap gap-6 mb-6 pb-6 border-b border-background-200/70">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">客服热线</p>
                        <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">400-888-9999</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">service@rejiujiu.com</p>
                      </div>
                    </div>
                  </div>

                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="text"
                      name="company_alt"
                      className="website-alt-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        联系电话
                      </label>
                      <input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的电话"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言，限500字"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      提交留言
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-3 bg-accent-100 text-accent-700 rounded-lg text-sm text-center">
                        感谢您的留言，我们会尽快与您联系！
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 bg-accent-100 text-accent-700 rounded-lg text-sm text-center">
                        {formError || '提交失败，请稍后重试'}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}